import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { getEcosystemItems } from '@/data/ecosystem';
import { getLive, isContentSettled, useLiveTick } from '@/lib/liveContent';
import { trackEvent } from '@/lib/analytics';
import { SectionHeader } from './SectionHeader';
import { Reveal } from './Reveal';
import { BrowserFrame, DemoCaption } from './compass/shared';
import { cn } from '@/lib/utils';

// Section 4 — Amden Systems showcase. Interactive tabs feature one system at
// a time (large screenshot, name, strapline, Explore link). Fully CMS-driven:
// names, straplines, screenshots and links resolve from the same published
// cms_listings records that power the product pages, so an admin edit to a
// product updates this section automatically.
const SYSTEM_ORDER = ['amden-compass', 'amden-pulse', 'amden-foresee', 'amden-reach'];

// Per-product accent, matching each product page's identity.
const ACCENTS = {
  'amden-compass': { tab: 'data-[active=true]:bg-gold-500 data-[active=true]:text-navy-950', dot: 'bg-gold-500' },
  'amden-pulse': { tab: 'data-[active=true]:bg-gold-500 data-[active=true]:text-navy-950', dot: 'bg-gold-500' },
  'amden-foresee': { tab: 'data-[active=true]:bg-indigo-500 data-[active=true]:text-white', dot: 'bg-indigo-400' },
  'amden-reach': { tab: 'data-[active=true]:bg-service-growth data-[active=true]:text-white', dot: 'bg-service-growth' },
};

const EASE = [0.22, 1, 0.36, 1];

const resolveSystems = () => {
  const items = getEcosystemItems();
  return SYSTEM_ORDER
    .map((slug) => items.find((it) => it.slug === slug))
    .filter((it) => it && it.status === 'published' && it.systemPage?.shots?.dashboard?.src && it.href);
};

export const SystemsShowcase = () => {
  useLiveTick();
  const settled = isContentSettled('listings');
  const systems = resolveSystems();
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();

  // Still waiting on the live listings fetch: hold the section shell.
  if (!settled && !getLive('listings')) {
    return (
      <section aria-label="Amden systems" data-testid="systems-showcase-loading" className="bg-navy-900 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl animate-pulse px-4 sm:px-6 lg:px-8" aria-hidden="true">
          <div className="mx-auto h-4 w-40 rounded bg-white/10" />
          <div className="mx-auto mt-4 h-8 w-80 max-w-full rounded bg-white/10" />
          <div className="mx-auto mt-12 h-72 max-w-4xl rounded-xl bg-white/5" />
        </div>
      </section>
    );
  }
  // Fetch settled but no publishable systems resolved (e.g. offline fallback
  // data): show a static signpost instead of vanishing silently.
  if (!systems.length) {
    return (
      <section aria-labelledby="systems-showcase-heading" data-testid="systems-showcase" className="relative overflow-hidden bg-navy-900 py-20 sm:py-28">
        <div className="grain-overlay" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            index="03"
            eyebrow="Amden Systems"
            title="Practical software, built from real client work"
            description="Four connected platforms designed from hands-on consultancy with Irish businesses — each one explored on its own page."
            dark
            testid="systems-showcase-header"
          />
          <div className="mt-12 text-center">
            <Link
              to="/ecosystem"
              className="inline-flex items-center gap-2 rounded-full bg-gold-500 px-6 py-3 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-400"
              data-testid="systems-showcase-fallback-cta"
            >
              Explore the Amden ecosystem <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const current = systems[Math.min(active, systems.length - 1)];
  const sp = current.systemPage;

  const onKeyDown = (e, i) => {
    let next = null;
    if (e.key === 'ArrowRight') next = (i + 1) % systems.length;
    if (e.key === 'ArrowLeft') next = (i - 1 + systems.length) % systems.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = systems.length - 1;
    if (next !== null) {
      e.preventDefault();
      setActive(next);
      document.getElementById(`systems-tab-${systems[next].slug}`)?.focus();
    }
  };

  return (
    <section aria-labelledby="systems-showcase-heading" data-testid="systems-showcase" className="relative overflow-hidden bg-navy-900 py-20 sm:py-28">
      <div className="grain-overlay" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="03"
          eyebrow="Amden Systems"
          title="Practical software, built from real client work"
          description="Four connected platforms designed from hands-on consultancy with Irish businesses — each one explored on its own page."
          dark
          testid="systems-showcase-header"
        />

        {/* Tabs: one per system. Roving tabindex + arrow-key navigation. */}
        <Reveal delay={0.08}>
          <div
            role="tablist"
            aria-label="Amden systems"
            className="mt-12 flex flex-wrap justify-center gap-2.5"
            data-testid="systems-showcase-tabs"
          >
            {systems.map((item, i) => {
              const isActive = i === active;
              const accent = ACCENTS[item.slug] || ACCENTS['amden-compass'];
              return (
                <button
                  key={item.slug}
                  id={`systems-tab-${item.slug}`}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`systems-panel-${item.slug}`}
                  tabIndex={isActive ? 0 : -1}
                  data-active={isActive}
                  data-testid={`systems-tab-${item.slug}`}
                  onClick={() => { setActive(i); trackEvent('systems_showcase_tab_selected'); }}
                  onKeyDown={(e) => onKeyDown(e, i)}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-bold transition-[background-color,border-color,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500',
                    isActive
                      ? accent.tab
                      : 'border-white/15 bg-white/5 text-slate-200 hover:border-white/30 hover:text-white'
                  )}
                >
                  <span className={cn('h-2 w-2 rounded-full', isActive ? 'bg-current opacity-70' : accent.dot)} aria-hidden="true" />
                  {item.name}
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* Panel: strapline + Explore link beside the large screenshot. */}
        <div className="mt-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.slug}
              id={`systems-panel-${current.slug}`}
              role="tabpanel"
              aria-labelledby={`systems-tab-${current.slug}`}
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0, y: -10 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="grid items-center gap-10 lg:grid-cols-[1fr_1.5fr]"
              data-testid={`systems-panel-${current.slug}`}
            >
              <div>
                <h3 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl" data-testid={`systems-name-${current.slug}`}>
                  {current.name}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-slate-300">
                  {sp.strapline || current.shortDescription}
                </p>
                <Link
                  to={current.href}
                  data-testid={`systems-explore-${current.slug}`}
                  onClick={() => trackEvent('systems_showcase_explore_click')}
                  className="group mt-7 inline-flex items-center gap-2 rounded-md bg-gold-500 px-6 py-3.5 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900"
                >
                  Explore {current.name}
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>
              <div data-testid={`systems-shot-${current.slug}`}>
                <BrowserFrame shot={sp.shots.dashboard} label={`${current.name} — demonstration workspace`} />
                <DemoCaption className="text-slate-500" />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
